import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  Mail, Send, RefreshCw, CheckCircle2, Clock, AlertCircle,
  Zap, Users, Building2, Star, Home, Heart, Leaf
} from 'lucide-react';

interface Backlink {
  id: string;
  website_name: string;
  website_url: string;
  contact_email: string | null;
  contact_name: string | null;
  category: string;
  company_type: string | null;
  outreach_status: string;
  follow_up_count: number | null;
  notes: string | null;
}

interface OutreachTemplate {
  id: string;
  name: string;
  category: string;
  icon: React.ReactNode;
  subject: string;
  body: string;
}

interface AutomatedOutreachSenderProps {
  userId: string;
}

const outreachTemplates: OutreachTemplate[] = [
  {
    id: 'review',
    name: 'Product Review Pitch',
    category: 'Review Site',
    icon: <Star className="h-4 w-4" />,
    subject: 'Partnership: Premium Smart Toilet Review for {{website_name}}',
    body: `Dear {{contact_name}},

I'm reaching out from Aureum VII Trading, Europe's premier supplier of luxury smart bathroom technology.

We'd love to offer {{website_name}} an exclusive review opportunity of our award-winning smart toilet collection, including:

• LUNA M9 - Our flagship bidet toilet (Special offer €600 per unit)
• Complimentary review unit
• High-resolution product imagery
• Direct access to our product team

Would you be interested in a review collaboration?

Best regards,
Aureum VII Trading
www.aureum7trading.com`
  },
  {
    id: 'architecture',
    name: 'Design Publication Pitch',
    category: 'Architecture/Design',
    icon: <Building2 className="h-4 w-4" />,
    subject: 'Guest Article: Smart Bathroom Design Trends for {{website_name}}',
    body: `Dear {{contact_name}},

As an avid reader of {{website_name}}, I'm writing to propose a guest article on "The Smart Bathroom Revolution in European Hospitality."

Our expertise at Aureum VII Trading spans luxury hotels and high-end residential projects across Europe. We can provide:

• Original photography from our installations
• Expert quotes from our design consultants
• Data on the hospitality smart bathroom market

Would you be interested in reviewing a draft?

Warm regards,
Aureum VII Trading`
  },
  {
    id: 'diy',
    name: 'DIY Installation Guide',
    category: 'DIY & Remodeling',
    icon: <Home className="h-4 w-4" />,
    subject: 'Expert Smart Toilet Installation Guide for {{website_name}}',
    body: `Hi {{contact_name}},

I noticed {{website_name}} provides excellent hands-on guides for home improvement enthusiasts.

At Aureum VII Trading, we've compiled comprehensive installation guides that could help your readers tackle smart toilet installations confidently:

• Step-by-step photography
• Technical diagrams
• Troubleshooting FAQs
• Video walkthrough options

These would be genuine educational content—not promotional pieces.

Interested in collaborating?

Best,
Aureum VII Trading`
  },
  {
    id: 'health',
    name: 'Health & Accessibility',
    category: 'Medical/Health',
    icon: <Heart className="h-4 w-4" />,
    subject: 'Guest Post: Bathroom Accessibility & Smart Toilets for {{website_name}}',
    body: `Dear {{contact_name}},

Your work at {{website_name}} on senior care and accessibility is truly valuable.

At Aureum VII Trading, we specialize in smart bathroom technology that improves quality of life for seniors and individuals with mobility challenges.

We'd love to contribute an article on "How Smart Toilets Support Independent Living" covering:

• Fall risk reduction with automatic features
• Hygiene benefits for limited mobility users
• Features that preserve dignity and independence

Would you consider a guest contribution?

Respectfully,
Aureum VII Trading`
  },
  {
    id: 'sustainability',
    name: 'Eco-Friendly Focus',
    category: 'Sustainability Blogs',
    icon: <Leaf className="h-4 w-4" />,
    subject: 'Data Article: Water Conservation in Bathroom Design for {{website_name}}',
    body: `Hello {{contact_name}},

Your work at {{website_name}} on sustainable living resonates with our mission at Aureum VII Trading.

We'd like to propose a data-driven article: "How Smart Toilets Cut Bathroom Water Use by 50%"

Key data points:
• Our smart toilets use 0.8-1.0 gallons vs. 1.6-3.5 gallons for standard toilets
• Annual savings: 5,000-10,000 gallons per household
• Integrated bidet eliminates toilet paper waste

This isn't a product pitch—it's environmental advocacy backed by third-party data.

Would this fit your editorial calendar?

Sustainably yours,
Aureum VII Trading`
  }
];

export const AutomatedOutreachSender = ({ userId }: AutomatedOutreachSenderProps) => {
  const [backlinks, setBacklinks] = useState<Backlink[]>([]);
  const [selectedBacklinks, setSelectedBacklinks] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('review');
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [senderName, setSenderName] = useState('Aureum VII Trading Team');
  const [senderEmail, setSenderEmail] = useState('info@aureum7trading.com');

  useEffect(() => {
    fetchBacklinks();
  }, [userId]);

  useEffect(() => {
    const template = outreachTemplates.find(t => t.id === selectedTemplate);
    if (template) {
      setCustomSubject(template.subject);
      setCustomBody(template.body);
    }
  }, [selectedTemplate]);

  const fetchBacklinks = async () => {
    try {
      const { data, error } = await supabase
        .from('backlinks')
        .select('*')
        .order('website_name');

      if (error) throw error;
      setBacklinks(data || []);
    } catch (error) {
      console.error('Error fetching backlinks:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBacklink = (id: string) => {
    setSelectedBacklinks(prev => 
      prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]
    );
  };

  const selectAllWithEmail = () => {
    const withEmail = backlinks.filter(b => b.contact_email && b.outreach_status === 'not_contacted');
    setSelectedBacklinks(withEmail.map(b => b.id));
  };

  const clearSelection = () => {
    setSelectedBacklinks([]);
  };

  const personalizeContent = (content: string, backlink: Backlink): string => {
    return content
      .replace(/\{\{website_name\}\}/g, backlink.website_name)
      .replace(/\{\{contact_name\}\}/g, backlink.contact_name || 'there')
      .replace(/\{\{category\}\}/g, backlink.category);
  };

  const sendOutreachEmails = async () => {
    if (selectedBacklinks.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    const selectedWithEmail = backlinks.filter(
      b => selectedBacklinks.includes(b.id) && b.contact_email
    );

    if (selectedWithEmail.length === 0) {
      toast.error('No selected recipients have email addresses');
      return;
    }

    setSending(true);
    let successCount = 0;
    let failCount = 0;

    for (const backlink of selectedWithEmail) {
      try {
        const personalizedSubject = personalizeContent(customSubject, backlink);
        const personalizedBody = personalizeContent(customBody, backlink);

        const { error } = await supabase.functions.invoke('send-email-template', {
          body: {
            to: backlink.contact_email,
            subject: personalizedSubject,
            html: `<div style="font-family: Arial, sans-serif; line-height: 1.6; white-space: pre-line;">${personalizedBody.replace(/\n/g, '<br>')}</div>`,
            from_name: senderName,
            reply_to: senderEmail
          }
        });

        if (error) throw error;

        // Update backlink status
        await supabase
          .from('backlinks')
          .update({
            outreach_status: 'contacted',
            outreach_date: new Date().toISOString(),
            last_outreach_date: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', backlink.id);

        successCount++;
      } catch (error) {
        console.error(`Failed to send to ${backlink.website_name}:`, error);
        failCount++;
      }
    }

    setSending(false);
    setSelectedBacklinks([]);
    fetchBacklinks();

    if (successCount > 0) {
      toast.success(`Successfully sent ${successCount} outreach email${successCount > 1 ? 's' : ''}`);
    }
    if (failCount > 0) {
      toast.error(`Failed to send ${failCount} email${failCount > 1 ? 's' : ''}`);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'contacted':
        return <Badge className="bg-blue-500"><Mail className="h-3 w-3 mr-1" />Contacted</Badge>;
      case 'follow_up':
        return <Badge className="bg-yellow-500"><Clock className="h-3 w-3 mr-1" />Follow-up</Badge>;
      case 'connected':
        return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" />Connected</Badge>;
      default:
        return <Badge variant="outline">Not Contacted</Badge>;
    }
  };

  const getCategoryIcon = (category: string) => {
    if (category.includes('Review')) return <Star className="h-4 w-4" />;
    if (category.includes('Architecture') || category.includes('Design')) return <Building2 className="h-4 w-4" />;
    if (category.includes('DIY') || category.includes('Remodel')) return <Home className="h-4 w-4" />;
    if (category.includes('Health') || category.includes('Medical')) return <Heart className="h-4 w-4" />;
    if (category.includes('Sustain')) return <Leaf className="h-4 w-4" />;
    return <Users className="h-4 w-4" />;
  };

  const notContactedWithEmail = backlinks.filter(b => b.contact_email && b.outreach_status === 'not_contacted');

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading backlink targets...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6" />
            Automated Outreach Sender
          </h2>
          <p className="text-muted-foreground">
            Send personalized outreach emails to backlink targets
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchBacklinks}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{backlinks.length}</div>
            <p className="text-xs text-muted-foreground">Total Targets</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-500">
              {backlinks.filter(b => b.contact_email).length}
            </div>
            <p className="text-xs text-muted-foreground">With Email</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-500">
              {backlinks.filter(b => b.outreach_status === 'contacted').length}
            </div>
            <p className="text-xs text-muted-foreground">Contacted</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-yellow-500">
              {notContactedWithEmail.length}
            </div>
            <p className="text-xs text-muted-foreground">Ready to Contact</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Email Template */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Template
            </CardTitle>
            <CardDescription>Customize your outreach message</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Template Type</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {outreachTemplates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex items-center gap-2">
                        {template.icon}
                        {template.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Sender Name</Label>
                <Input 
                  value={senderName} 
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Your Name"
                />
              </div>
              <div>
                <Label>Reply-to Email</Label>
                <Input 
                  value={senderEmail} 
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <Label>Subject Line</Label>
              <Input 
                value={customSubject} 
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="Email subject..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Variables: {"{{website_name}}"}, {"{{contact_name}}"}, {"{{category}}"}
              </p>
            </div>

            <div>
              <Label>Email Body</Label>
              <Textarea 
                value={customBody} 
                onChange={(e) => setCustomBody(e.target.value)}
                placeholder="Email content..."
                className="min-h-[200px] font-mono text-sm"
              />
            </div>

            <Button 
              className="w-full" 
              onClick={sendOutreachEmails}
              disabled={sending || selectedBacklinks.length === 0}
            >
              {sending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {selectedBacklinks.length} Selected Target{selectedBacklinks.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Recipients */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Select Recipients
            </CardTitle>
            <CardDescription>
              {selectedBacklinks.length} of {backlinks.length} selected
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={selectAllWithEmail}>
                Select Ready ({notContactedWithEmail.length})
              </Button>
              <Button variant="outline" size="sm" onClick={clearSelection}>
                Clear
              </Button>
            </div>

            <div className="rounded-md border max-h-[400px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Website</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backlinks.map((backlink) => (
                    <TableRow 
                      key={backlink.id}
                      className={!backlink.contact_email ? 'opacity-50' : ''}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedBacklinks.includes(backlink.id)}
                          onCheckedChange={() => toggleBacklink(backlink.id)}
                          disabled={!backlink.contact_email}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{backlink.website_name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                            {backlink.contact_email || 'No email'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {getCategoryIcon(backlink.category)}
                          <span className="text-sm">{backlink.category}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(backlink.outreach_status)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {notContactedWithEmail.length === 0 && backlinks.length > 0 && (
              <div className="text-center py-4 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>All targets with emails have been contacted</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
